// A purchase window for the audience rules (C2, Gio 2026-10-17): "in the last N days", any N
// back to January 1, 2021, or "between" two New York calendar days, the second of which may
// be "today". Used by Last purchase and by Production purchased. The value is the
// `PurchaseWindow` of `lib/purchase-window.ts`; the rule codec turns it into API rules.

import { useId } from 'react';
import {
  PURCHASE_HISTORY_START,
  PURCHASE_WINDOW_CHIPS,
  SINCE_HISTORY_START,
  dayMinus,
  daysBetween,
  formatPurchaseDay,
  maxRollingDays,
  purchaseWindowFromValue,
  todayInNewYork,
  type PurchaseWindow,
} from '@/lib/purchase-window';

export interface PurchaseWindowPickerProps {
  /** The rule row's stored value: a `PurchaseWindow`, or a legacy label such as `'2 years'`. */
  value: unknown;
  onChange: (window: PurchaseWindow) => void;
  /** Names the controls for assistive tech ("Last purchase", "Production purchased"). */
  label: string;
  /** Last purchase only: a closed range leaves out people who bought again after it. */
  lapsedNote?: boolean;
  /** Fixed for tests; the browser's New York day otherwise. */
  today?: string;
}

export function PurchaseWindowPicker({
  value,
  onChange,
  label,
  lapsedNote = false,
  today = todayInNewYork(),
}: PurchaseWindowPickerProps) {
  const window = purchaseWindowFromValue(value);
  const id = useId();
  const maxDays = maxRollingDays(today);
  const since2021 = window.mode === 'between' && window.from === PURCHASE_HISTORY_START && window.to === null;

  const toBetween = () => {
    if (window.mode === 'between') return;
    const days = Math.min(Math.max(window.days, 1), maxDays);
    const from = dayMinus(today, days);
    onChange({ mode: 'between', from: from < PURCHASE_HISTORY_START ? PURCHASE_HISTORY_START : from, to: null });
  };
  const toLast = () => {
    if (window.mode === 'last') return;
    const days = window.from ? daysBetween(window.from, today) : 730;
    onChange({ mode: 'last', days: Math.min(Math.max(days, 1), maxDays) });
  };

  return (
    <div className="pw" role="group" aria-label={`${label} window`}>
      <div className="pw-row">
        <div className="seg pw-seg" role="group" aria-label={`${label}: how the window is set`}>
          <button type="button" aria-pressed={window.mode === 'last'} className={window.mode === 'last' ? 'on' : ''} onClick={toLast}>
            In the last
          </button>
          <button type="button" aria-pressed={window.mode === 'between'} className={window.mode === 'between' ? 'on' : ''} onClick={toBetween}>
            Between
          </button>
        </div>
        {window.mode === 'last' ? (
          <label className="pw-days">
            <input
              className="sel pw-number"
              type="number"
              inputMode="numeric"
              min={1}
              max={maxDays}
              step={1}
              aria-label={`${label}: number of days`}
              value={window.days > 0 ? window.days : ''}
              onChange={(event) => onChange({ mode: 'last', days: Number.parseInt(event.target.value, 10) || 0 })}
            />
            <span className="muted">days</span>
          </label>
        ) : (
          <span className="pw-dates">
            <input
              className="sel pw-date"
              type="date"
              min={PURCHASE_HISTORY_START}
              max={window.to || today}
              aria-label={`${label}: first day`}
              value={window.from}
              onChange={(event) => onChange({ ...window, from: event.target.value })}
            />
            <span className="muted">to</span>
            <input
              className="sel pw-date"
              type="date"
              min={window.from || PURCHASE_HISTORY_START}
              max={today}
              aria-label={`${label}: last day`}
              disabled={window.to === null}
              value={window.to ?? today}
              onChange={(event) => onChange({ ...window, to: event.target.value })}
            />
            <label className="pw-today" htmlFor={`${id}-today`}>
              <input
                id={`${id}-today`}
                type="checkbox"
                checked={window.to === null}
                onChange={(event) => onChange({ ...window, to: event.target.checked ? null : today })}
              />
              today
            </label>
          </span>
        )}
      </div>
      <div className="pw-chips" role="group" aria-label={`${label}: common windows`}>
        {PURCHASE_WINDOW_CHIPS.map((chip) => (
          <button
            key={chip.days}
            type="button"
            className={`chip pw-chip${window.mode === 'last' && window.days === chip.days ? ' active' : ''}`}
            aria-pressed={window.mode === 'last' && window.days === chip.days}
            onClick={() => onChange({ mode: 'last', days: chip.days })}
          >
            {chip.label}
          </button>
        ))}
        <button
          type="button"
          className={`chip pw-chip${since2021 ? ' active' : ''}`}
          aria-pressed={since2021}
          onClick={() => onChange(SINCE_HISTORY_START)}
        >
          Since Jan 2021
        </button>
      </div>
      {lapsedNote && window.mode === 'between' && window.to && (
        <div className="muted pw-note">
          People who bought again after {formatPurchaseDay(window.to)} are not included.
        </div>
      )}
    </div>
  );
}
